import React, { useEffect, useRef, useState } from "react";
import useTheme from "../ColorConfig/usetheme";
import ImagesSection from "./InofoSub/ImageSec";
import ContentSection from "./InofoSub/ContectSec";

export default function Info({ title, description }) {
  const colors = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [titleAnimated, setTitleAnimated] = useState(false);
  const [descriptionAnimated, setDescriptionAnimated] = useState(false);
  const [firstImageAnimated, setFirstImageAnimated] = useState(false);
  const [secondImageAnimated, setSecondImageAnimated] = useState(false);
  const [floatingElementsAnimated, setFloatingElementsAnimated] =
    useState(false);
  const [textOverlaysAnimated, setTextOverlaysAnimated] = useState(false);
  const sectionRef = useRef(null);

  const displayTitle = title || "About IDLBAY Private Limited";
  const displayDescription =
    description ||
    "At IDLBAY, we are dedicated to empowering businesses through cutting-edge IT solutions and services. We represents the strategic evolution from our foundation as Dremerz Consultancy and Services. With a focus on innovation, quality, and client satisfaction, we partner with you to navigate the complexities of the digital landscape and achieve your strategic objectives.";

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);

          // ULTRA FAST staggered animations - reduced to 75ms intervals
          setTimeout(() => setFirstImageAnimated(true), 75); // was 150
          setTimeout(() => setSecondImageAnimated(true), 150); // was 300
          setTimeout(() => setTitleAnimated(true), 225); // was 450
          setTimeout(() => setDescriptionAnimated(true), 300); // was 600
          setTimeout(() => setFloatingElementsAnimated(true), 375); // was 750
          setTimeout(() => setTextOverlaysAnimated(true), 450); // was 900

          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <div
      className={`info-section ${isVisible ? "animate-in" : ""}`}
      ref={sectionRef}
    >
      <div className="info-container">
        <div className="content-wrapper">
          {/* Left Side - Overlapping Images */}
          <ImagesSection
            firstImageAnimated={firstImageAnimated}
            secondImageAnimated={secondImageAnimated}
            floatingElementsAnimated={floatingElementsAnimated}
            textOverlaysAnimated={textOverlaysAnimated}
          />

          {/* Right Side - Content */}
          <ContentSection
            title={displayTitle}
            description={displayDescription}
            titleAnimated={titleAnimated}
            descriptionAnimated={descriptionAnimated}
          />
        </div>
      </div>

      <style jsx>{`
        .info-section {
          padding: 1rem 2rem;
          background: linear-gradient(
            135deg,
            var(--color-white) 0%,
            var(--color-whiteTransparent) 50%,
            var(--color-white) 100%
          );
          position: relative;
          overflow: hidden;
          opacity: 0;
          transform: translateY(60px);
          transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94); /* Faster: 0.5s instead of 0.8s */
          height: auto;
          max-height: 90vh;
          padding-top: -30px;
        }

        .info-section.animate-in {
          opacity: 1;
          transform: translateY(0);
        }

        .info-container {
          max-width: 1400px;
          margin: 0 auto;
          position: relative;
          height: 100%;
        }

        .content-wrapper {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          align-items: center;
          min-height: 500px;
          height: 100%;
        }

        /* Images Section - FASTER ANIMATIONS */
        .images-section {
          position: relative;
          height: 450px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .image-container {
          position: absolute;
          transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1); /* Faster: 0.4s instead of 0.6s */
          opacity: 0;
          margin-top: 60px;
        }

        .first-image {
          top: 0;
          left: 0;
          z-index: 2;
          transform: translateX(-100px) translateY(50px) rotate(-10deg)
            scale(0.8);
          cursor: none;
        }

        .first-image.animate-first {
          opacity: 1;
          transform: translateX(0) translateY(0) rotate(-8deg) scale(1);
        }

        .second-image {
          bottom: 0;
          right: 0;
          z-index: 3;
          transform: translateX(100px) translateY(-50px) rotate(15deg)
            scale(0.8);
          margin-right: 30px;
        }

        .second-image.animate-second {
          opacity: 1;
          transform: translateX(-20px) translateY(-20px) rotate(8deg) scale(1);
        }

        /* Text Overlays - FASTER */
        .text-overlay {
          position: absolute;
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94); /* Faster: 0.4s instead of 0.6s */
          z-index: 1;
          pointer-events: none;
        }

        .text-overlay.animate-text {
          opacity: 1;
          transform: translateY(0);
        }

        .right-overlay {
          top: 15%;
          right: -10px;
          text-align: right;
        }

        .left-overlay {
          bottom: 10%;
          left: -10px;
          text-align: left;
        }

        .overlay-content {
          background: var(--color-primary);
          backdrop-filter: blur(10px);
          padding: 1rem 1.5rem;
          border-radius: 15px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }

        .overlay-title {
          color: var(--color-white);
          font-size: 1.2rem;
          font-weight: 700;
          margin: 0 0 0.5rem 0;
        }

        .overlay-text {
          color: var(--color-white);
          font-size: 0.9rem;
          margin: 0;
          opacity: 0.8;
        }

        .image-frame {
          position: relative;
          width: 280px;
          height: 200px;
          border-radius: 25px;
          overflow: hidden;
          box-shadow: 0 25px 50px rgba(0, 0, 0, 0.15),
            0 10px 30px rgba(0, 0, 0, 0.1);
          transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94); /* Faster: 0.3s instead of 0.5s */
          cursor: pointer;
        }

        .image-frame:hover {
          transform: scale(1.05) rotate(0deg);
          box-shadow: 0 35px 70px rgba(0, 0, 0, 0.2),
            0 15px 40px rgba(0, 0, 0, 0.15);
        }

        .main-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease; /* Faster: 0.3s instead of 0.5s */
        }

        .image-frame:hover .main-image {
          transform: scale(1.1);
        }

        .image-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          transition: opacity 0.2s ease; /* Faster: 0.2s instead of 0.3s */
        }

        .first-overlay {
          background: linear-gradient(
            135deg,
            var(--color-primaryTransparent) 0%,
            transparent 60%
          );
          opacity: 0.4;
        }

        .second-overlay {
          background: linear-gradient(
            225deg,
            rgba(255, 107, 107, 0.3) 0%,
            transparent 60%
          );
          opacity: 0.3;
        }

        .shimmer-effect {
          position: absolute;
          top: -50%;
          left: -50%;
          width: 200%;
          height: 200%;
          background: linear-gradient(
            45deg,
            transparent 30%,
            rgba(255, 255, 255, 0.3) 50%,
            transparent 70%
          );
          transform: translateX(-100%) translateY(-100%) rotate(45deg);
          transition: transform 0.5s ease; /* Faster: 0.5s instead of 0.8s */
        }

        .image-frame:hover .shimmer-effect {
          transform: translateX(100%) translateY(100%) rotate(45deg);
        }

        /* Floating Elements - FASTER */
        .floating-elements {
          position: absolute;
          width: 100%;
          height: 100%;
          pointer-events: none;
          opacity: 0;
          transition: opacity 0.6s ease; /* Faster: 0.6s instead of 1s */
        }

        .floating-elements.animate-float {
          opacity: 1;
        }

        .float-circle {
          position: absolute;
          border-radius: 50%;
          animation: float 4s ease-in-out infinite; /* Faster: 4s instead of 6s */
        }

        .float-1 {
          width: 20px;
          height: 20px;
          background: var(--color-primary);
          top: 20%;
          right: 10%;
          animation-delay: 0s;
        }

        .float-2 {
          width: 12px;
          height: 12px;
          background: var(--color-primaryLight);
          top: 80%;
          left: 10%;
          animation-delay: 1s; /* Faster: 1s instead of 2s */
        }

        .float-3 {
          width: 16px;
          height: 16px;
          background: var(--color-primaryHover);
          bottom: 90%;
          right: 90%;
          animation-delay: 2s; /* Faster: 2s instead of 4s */
        }

        /* Content Section - FASTER ANIMATIONS */
        .content-section {
          position: relative;
        }

        .title-container {
          margin-bottom: 2rem;
          opacity: 0;
          transform: translateX(60px);
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94); /* Faster: 0.4s instead of 0.6s */
        }

        .title-container.slide-in-right {
          opacity: 1;
          transform: translateX(0);
        }

        .info-title {
          color: var(--color-primary);
          font-size: 3rem;
          font-weight: 800;
          line-height: 1.2;
          margin-bottom: 1rem;
          position: relative;
        }

        .title-underline {
          width: 0;
          height: 4px;
          background: linear-gradient(
            90deg,
            var(--color-primary) 0%,
            var(--color-primaryLight) 100%
          );
          border-radius: 2px;
          transition: width 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s; /* Faster: 0.5s instead of 0.8s */
        }

        .title-container.slide-in-right .title-underline {
          width: 100px;
        }

        .description-container {
          opacity: 0;
          transform: translateX(60px);
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s; /* Faster: 0.4s instead of 0.6s */
          position: relative;
        }

        .description-container.slide-in-right {
          opacity: 1;
          transform: translateX(0);
        }

        .info-description {
          color: var(--color-bodyBlack);
          font-size: 1.3rem;
          line-height: 1.8;
          font-weight: 400;
          position: relative;
          padding-left: 20px;
        }

        .content-accent {
          position: absolute;
          left: 0;
          top: 0;
          width: 4px;
          height: 0;
          background: linear-gradient(
            180deg,
            var(--color-primary) 0%,
            var(--color-primaryLight) 100%
          );
          border-radius: 2px;
          transition: height 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.6s; /* Faster: 0.6s instead of 1s */
        }

        .description-container.slide-in-right .content-accent {
          height: 60px;
        }

        /* Interactive Elements - FASTER */
        .interactive-elements {
          opacity: 0;
          transform: translateX(60px);
          transition: all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94) 0.2s; /* Faster: 0.4s instead of 0.6s */
          position: relative;
          height: 40px;
        }

        .interactive-elements.slide-in-right {
          opacity: 1;
          transform: translateX(0);
        }

        .pulse-indicator {
          width: 12px;
          height: 12px;
          background: var(--color-primary);
          border-radius: 50%;
          position: absolute;
          left: 0;
          top: 50%;
          transform: translateY(-50%);
          animation: pulse 1.5s ease-in-out infinite; /* Faster: 1.5s instead of 2s */
        }

        .gradient-line {
          position: absolute;
          left: 30px;
          top: 50%;
          transform: translateY(-50%);
          width: 0;
          height: 2px;
          background: linear-gradient(
            90deg,
            var(--color-primary) 0%,
            transparent 100%
          );
          transition: width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) 1s; /* Faster: 0.6s instead of 1s */
        }

        .interactive-elements.slide-in-right .gradient-line {
          width: 200px;
        }

        /* Background Effects - FASTER */
        .info-section::before {
          content: "";
          position: absolute;
          top: 20%;
          right: 10%;
          width: 400px;
          height: 400px;
          background: radial-gradient(
            circle,
            var(--color-primaryTransparent) 0%,
            transparent 70%
          );
          border-radius: 50%;
          z-index: -1;
          opacity: 0;
          transition: opacity 0.6s ease 0.3s; /* Faster: 0.6s instead of 1s */
        }

        .info-section.animate-in::before {
          opacity: 0.3;
        }

        /* Animations - FASTER */
        @keyframes float {
          0%,
          100% {
            transform: translateY(0px) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        @keyframes pulse {
          0%,
          100% {
            transform: translateY(-50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translateY(-50%) scale(1.5);
            opacity: 0.7;
          }
        }

        /* Responsive Design */
        @media (max-width: 1200px) {
          .content-wrapper {
            gap: 3rem;
          }

          .info-title {
            font-size: 2.5rem;
          }

          .image-frame {
            width: 250px;
            height: 180px;
          }
        }

        @media (max-width: 968px) {
          .info-section {
            height: auto;
            max-height: none;
            padding: 4rem 2rem;
          }

          .content-wrapper {
            grid-template-columns: 1fr;
            gap: 4rem;
            text-align: center;
            min-height: auto;
          }

          .images-section {
            order: 2;
            height: 450px;
            margin-top: 2rem;
          }

          .content-section {
            order: 1;
          }

          .info-title {
            font-size: 2.2rem;
          }

          .info-description {
            font-size: 1.2rem;
            text-align: left;
          }

          /* Mobile Text Overlay Repositioning */
          .right-overlay {
            top: -60px;
            right: 0;
            left: auto;
            text-align: center;
          }

          .left-overlay {
            top: -120px;
            left: 0;
            right: auto;
            bottom: auto;
            text-align: center;
          }

          .overlay-content {
            padding: 0.8rem 1.2rem;
            margin: 0 auto;
            max-width: 250px;
          }

          .overlay-title {
            font-size: 1.1rem;
          }

          .overlay-text {
            font-size: 0.85rem;
          }
        }

        @media (max-width: 768px) {
          .info-section {
            padding: 3rem 1.5rem;
            height: auto;
            max-height: none;
          }

          .images-section {
            height: 400px;
            margin-top: 1rem;
          }

          .image-frame {
            width: 220px;
            height: 160px;
          }

          .first-image.animate-first {
            transform: translateX(10px) translateY(10px) rotate(-5deg) scale(1);
          }

          .second-image.animate-second {
            transform: translateX(-10px) translateY(-10px) rotate(5deg) scale(1);
          }

          .info-title {
            font-size: 2rem;
          }

          .info-description {
            font-size: 1.1rem;
          }

          /* Adjust overlay positioning for smaller screens */
          .right-overlay {
            top: -50px;
          }

          .left-overlay {
            top: -100px;
          }

          .overlay-content {
            padding: 0.7rem 1rem;
            max-width: 220px;
          }

          .overlay-title {
            font-size: 1rem;
          }

          .overlay-text {
            font-size: 0.8rem;
          }
        }

        @media (max-width: 480px) {
          .info-section {
            padding: 2.5rem 1rem;
          }

          .images-section {
            height: 350px;
          }

          .image-frame {
            width: 180px;
            height: 130px;
          }

          .info-title {
            font-size: 1.8rem;
          }

          .info-description {
            font-size: 1rem;
            padding-left: 15px;
          }

          .content-wrapper {
            gap: 2rem;
          }

          /* Further adjust overlays for mobile */
          .right-overlay {
            top: -40px;
          }

          .left-overlay {
            top: -80px;
          }

          .overlay-content {
            padding: 0.6rem 0.8rem;
            max-width: 180px;
          }

          .overlay-title {
            font-size: 0.9rem;
            margin-bottom: 0.3rem;
          }

          .overlay-text {
            font-size: 0.75rem;
          }
        }
      `}</style>
    </div>
  );
}
